/**
 * Shared validation helpers so that business rules stay consistent across modules.
 */

const CUIT_MULTIPLIERS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

export const normalizeString = (value) => {
  if (!value) return '';
  return value
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

export const validateEmail = (email, { optional = false } = {}) => {
  if (!email) return optional;
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return pattern.test(email);
};

export const validatePhone = (phone, { optional = true, minLength = 8, maxLength = 15 } = {}) => {
  if (!phone) return optional;
  const clean = phone.toString().replace(/\D/g, '');
  return clean.length >= minLength && clean.length <= maxLength;
};

export const validateDNI = (dni) => {
  if (!dni) return false;
  const clean = dni.toString().replace(/\D/g, '');
  return clean.length >= 7 && clean.length <= 8;
};

export const calculateCuitCheckDigit = (cuitWithoutDigit) => {
  return CUIT_MULTIPLIERS.reduce((acc, multiplier, index) => {
    return acc + Number.parseInt(cuitWithoutDigit[index] ?? '0', 10) * multiplier;
  }, 0);
};

export const computeCuitCheckDigit = (cuitWithoutDigit) => {
  const sum = calculateCuitCheckDigit(cuitWithoutDigit);
  const remainder = sum % 11;

  if (remainder === 0) return 0;
  if (remainder === 1) {
    const prefix = cuitWithoutDigit.slice(0, 2);
    if (prefix === '20') return 9;
    if (prefix === '27') return 4;
    return 1;
  }
  return 11 - remainder;
};

export const validateCUIT = (cuit) => {
  if (!cuit) return false;
  const clean = cuit.toString().replace(/\D/g, '');
  if (clean.length !== 11) return false;

  const expected = computeCuitCheckDigit(clean.substring(0, 10));
  return Number.parseInt(clean[10], 10) === expected;
};

export const generateCUIT = (dni, gender = 'h') => {
  if (!dni) return '';

  const prefijo = gender === 'm' ? '27' : gender === 'O' ? '23' : '20';
  const normalizedDni = dni.toString().padStart(8, '0');
  const base = `${prefijo}${normalizedDni}`;
  const digit = computeCuitCheckDigit(base);

  return `${base}${digit}`;
};
